import axios from 'axios';
import JSZip from 'jszip';

import { getInstance } from '../../api/instance';
import {
  OperatorIdentity,
  operatorRequestConfig,
} from '../../api/operatorHeaders';
import { recipientName } from './helpers';
import {
  ApproveTicketPayload,
  AssetKind,
  BookCancelPreview,
  CancelTicketPayload,
  CorrectTicketStatusPayload,
  FlipBookReplaceAccepted,
  Paginated,
  SettleTicketRefundPayload,
  TicketCounts,
  TicketDetail,
  TicketRecipient,
  TicketFileDownload,
  TicketFileKind,
  TicketFileStatus,
  TicketOrigin,
  TicketStatusFilter,
  TicketSummary,
  UpdateShipToPayload,
  UpdateShipToResult,
  UpdateTicketStatusPayload,
} from './types';

/**
 * Print-shop ticket calls for the DS admin MOD contract.
 *
 * The shared `dsInstance` baseURL already carries the `/v1` prefix (see
 * `src/modules/api/instance.ts` and the legacy `api/mail-on-demand.ts`), so —
 * like the rest of the customer-facing routes — these paths are relative and
 * unprefixed: `abo/mod/tickets` resolves to `/v1/abo/mod/tickets`.
 *
 * Paging contract: the UI is 1-based, the DS paginator is 0-indexed. The
 * translation (`page - 1`) lives here so the pages never deal with it.
 */
export const TICKETS_PATH = 'abo/mod/tickets';
export const TICKET_COUNTS_PATH = `${TICKETS_PATH}/counts`;

export const DEFAULT_PAGE_SIZE = 25;

/** Page-size options for the recipients table selector (task 47); capped at 100. */
export const RECIPIENTS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const instance = getInstance();

export interface TicketQueueParams {
  status?: TicketStatusFilter;
  assetKind?: AssetKind;
  /** Which tab is asking. Omitted means "both kinds", the pre-tabs behaviour. */
  origin?: TicketOrigin;
  /**
   * Leave the `imported` archive out of the All view (DS `excludeImported`,
   * abo-print-shop-menu-split ADR-002). Sent only with status `all`: a
   * specific status tab already names exactly what it wants.
   */
  excludeImported?: boolean;
  search?: string;
  page?: number; // 1-based UI page
  limit?: number;
}

/**
 * Queue listing; backend returns oldest-paid-first and a `{ data, meta }`
 * envelope. The status param is omitted for "all actionable".
 */
export const fetchTickets = async (
  params: TicketQueueParams = {},
): Promise<Paginated<TicketSummary>> => {
  const {
    status = 'all',
    assetKind,
    origin,
    excludeImported = false,
    search = '',
    page = 1,
    limit = DEFAULT_PAGE_SIZE,
  } = params;
  const query: Record<string, string | number> = {
    page: Math.max(0, page - 1), // UI 1-based → DS 0-based
    limit,
  };
  if (status !== 'all') {
    query.status = status;
  } else if (excludeImported) {
    query.excludeImported = 'true';
  }
  if (assetKind) {
    query.assetKind = assetKind;
  }
  if (origin) {
    query.origin = origin;
  }
  const trimmed = search.trim();
  if (trimmed !== '') {
    query.search = trimmed;
  }
  const { data } = await instance.get<Paginated<TicketSummary>>(TICKETS_PATH, {
    params: query,
  });
  return data;
};

/**
 * Per-status totals for the queue tabs, scoped to one tab.
 *
 * Scoped so each tab's badges count ITS OWN work — an unscoped call would show
 * the mailing backlog on the Books tab. Omitting `origin` keeps the original
 * both-kinds behaviour.
 */
export const fetchTicketCounts = async (
  origin?: TicketOrigin,
): Promise<TicketCounts> => {
  // The unscoped call is issued EXACTLY as it was before this parameter
  // existed — no trailing config object — so nothing about the original
  // both-kinds behaviour changes, on the wire or at the call site.
  const { data } = origin
    ? await instance.get<TicketCounts>(TICKET_COUNTS_PATH, {
        params: { origin },
      })
    : await instance.get<TicketCounts>(TICKET_COUNTS_PATH);
  return data;
};

/**
 * Find an order by the number a member quotes, or by their email.
 *
 * A DIFFERENT surface from the queue, deliberately (ADR-005). The queue lists
 * work, in a status set that excludes the archive; this reaches every order
 * whatever its status, so it answers "what happened with 0000071234?" for a
 * record that is four years old.
 */
export const lookupTickets = async (params: {
  orderNumber?: string;
  email?: string;
  page?: number;
  limit?: number;
}): Promise<Paginated<TicketSummary>> => {
  const query: Record<string, string | number> = {
    page: Math.max(0, (params.page ?? 1) - 1), // UI 1-based → DS 0-based
    limit: params.limit ?? DEFAULT_PAGE_SIZE,
  };
  if (params.orderNumber) {
    query.orderNumber = params.orderNumber;
  }
  if (params.email) {
    query.email = params.email;
  }
  const { data } = await instance.get<Paginated<TicketSummary>>(
    `${TICKETS_PATH}/lookup`,
    { params: query },
  );
  return data;
};

/** Full ticket detail; the backend 404s for orders that have not been paid. */
export const fetchTicket = async (orderId: string): Promise<TicketDetail> => {
  const { data } = await instance.get<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}`,
  );
  return data;
};

export interface RecipientsParams {
  skipped?: boolean;
  page?: number; // 1-based UI page
  limit?: number;
}

/**
 * Inline recipients for the detail page: the mailing set by default,
 * the excluded rows when `skipped` is true. CSV stays the bulk export.
 */
export const fetchTicketRecipients = async (
  orderId: string,
  params: RecipientsParams = {},
): Promise<Paginated<TicketRecipient>> => {
  const { skipped = false, page = 1, limit = DEFAULT_PAGE_SIZE } = params;
  const { data } = await instance.get<Paginated<TicketRecipient>>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/recipients`,
    { params: { skipped, page: Math.max(0, page - 1), limit } },
  );
  return data;
};

/**
 * Mailable-recipients CSV as a blob so the api-key auth header is respected;
 * callers turn it into an object-URL download (see `downloadBlob`).
 */
export const fetchRecipientsCsv = async (orderId: string): Promise<Blob> => {
  const { data } = await instance.get<Blob>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/recipients.csv`,
    { responseType: 'blob' },
  );
  return data;
};

export interface RenderedLetter {
  name: string;
  url: string;
}

/**
 * Collects every RENDERED per-recipient cover letter across ALL recipient
 * pages (the detail table is paginated; the bundle covers the whole mailing).
 * Requests the max page size and follows `meta.lastPage`.
 */
export const collectRenderedLetters = async (
  orderId: string,
): Promise<RenderedLetter[]> => {
  const out: RenderedLetter[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    // eslint-disable-next-line no-await-in-loop -- sequential paging by design
    const res = await fetchTicketRecipients(orderId, {
      skipped: false,
      page,
      limit: 100,
    });
    res.data.forEach((recipient) => {
      if (
        recipient.coverLetterStatus === 'rendered' &&
        recipient.coverLetterPdfUrl
      ) {
        out.push({
          name: recipientName(recipient),
          url: recipient.coverLetterPdfUrl,
        });
      }
    });
    lastPage = res.meta?.lastPage ?? 1;
    page += 1;
  } while (page <= lastPage);
  return out;
};

/** Filesystem-safe, index-prefixed filename so duplicate names never collide. */
const letterFileName = (name: string, index: number): string => {
  const slug =
    name
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'recipient';
  return `${String(index + 1).padStart(3, '0')}-${slug}.pdf`;
};

/**
 * Builds one zip of the given letters by fetching each PDF directly from its
 * (public) S3 URL in the browser and packaging them with JSZip — avoids a
 * server-side archiver dependency (intentionally dropped on the DS side).
 */
export const buildLettersZip = async (
  letters: RenderedLetter[],
): Promise<Blob> => {
  const zip = new JSZip();
  await Promise.all(
    letters.map(async (letter, index) => {
      const { data } = await axios.get<Blob>(letter.url, {
        responseType: 'blob',
      });
      zip.file(letterFileName(letter.name, index), data);
    }),
  );
  return zip.generateAsync({ type: 'blob' });
};

/** Status transition; the backend answers 409 with a message on illegal actions. */
export const updateTicketStatus = async (
  orderId: string,
  payload: UpdateTicketStatusPayload,
): Promise<TicketDetail> => {
  const body: UpdateTicketStatusPayload = { action: payload.action };
  if (payload.trackingRef && payload.trackingRef.trim() !== '') {
    body.trackingRef = payload.trackingRef.trim();
  }
  const { data } = await instance.post<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/status`,
    body,
  );
  return data;
};

/**
 * Corrects a ticket's status (task 60) — a deliberate override of the forward
 * state machine to fix a wrongly-set status, distinct from the forward action.
 * The DS endpoint (task 59) constrains the target to the production lane,
 * records a `status_corrected` audit entry, does NOT notify the member or move
 * credits, and returns the updated ticket in the same shape as `fetchTicket`.
 */
export const correctTicketStatus = async (
  orderId: string,
  payload: CorrectTicketStatusPayload,
): Promise<TicketDetail> => {
  const body: CorrectTicketStatusPayload = { status: payload.status };
  const reason = payload.reason?.trim();
  if (reason) {
    body.reason = reason;
  }
  const { data } = await instance.post<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/status/correct`,
    body,
  );
  return data;
};

/**
 * Corrects a ticket's tracking reference after it has been set (task 53).
 * Distinct from the ship transition: this is a field-only update (no status
 * change). The DS endpoint records a `tracking_updated` audit entry and
 * returns the updated ticket in the same shape as `fetchTicket`, so the caller
 * can refresh the detail (value + new history entry) from the response.
 */
export const updateTicketTracking = async (
  orderId: string,
  trackingRef: string,
): Promise<TicketDetail> => {
  const { data } = await instance.post<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/tracking`,
    { trackingRef: trackingRef.trim() },
  );
  return data;
};

/**
 * Manually re-triggers a terminally-failed per-recipient cover-letter render
 * (mod-cover-letter-manual-retry). Unlike `updateTicketTracking`/
 * `correctTicketStatus` (which return the full ticket), this DS endpoint
 * returns just the recipient row (`toRecipientRow`) — the same shape already
 * used by `fetchTicketRecipients`'s `data` array.
 */
export const retryCoverLetter = async (
  orderId: string,
  recipientId: string,
): Promise<TicketRecipient> => {
  const { data } = await instance.post<TicketRecipient>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/recipients/${encodeURIComponent(recipientId)}/retry-cover-letter`,
    {},
  );
  return data;
};

/**
 * Mints a short-lived signed URL for one print file of a book ticket.
 *
 * Called PER CLICK and never cached (ADR-006): the URL expires, so a stored one
 * becomes a dead link on exactly the tickets that sit longest in the queue, and
 * it is a bearer credential that should not outlive its use.
 *
 * A file that is not ready answers 409 rather than a URL — the caller surfaces
 * that as a state, never as a broken link.
 */
export const fetchTicketFileDownload = async (
  orderId: string,
  kind: TicketFileKind,
): Promise<TicketFileDownload> => {
  const { data } = await instance.get<TicketFileDownload>(
    `${TICKETS_PATH}/${orderId}/files/${kind}/download`,
  );
  return data;
};

/**
 * Asks DS to produce the invoice for a book ticket that has none.
 *
 * The invoice is best-effort at ingestion, so a transient failure of the
 * invoice service leaves the ticket standing without one — right for the ticket
 * and a dead end for the operator, who saw "never produced for this order" and
 * had nothing to do about it. Safe to repeat.
 */
export const regenerateTicketInvoice = async (
  orderId: string,
  operator: OperatorIdentity,
): Promise<TicketFileStatus> => {
  const { data } = await instance.post<TicketFileStatus>(
    `${TICKETS_PATH}/${orderId}/files/invoice/regenerate`,
    undefined,
    operatorRequestConfig(operator),
  );
  return data;
};

/**
 * Posts one file as `multipart/form-data` (field `file`) with the operator's
 * id token, reporting upload progress. Shared by the print-file, flip book and
 * ITP uploads.
 */
const postFile = async <T>(
  path: string,
  file: File,
  operator: OperatorIdentity,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<T> => {
  const body = new FormData();
  body.append('file', file);
  const { data } = await instance.post<T>(path, body, {
    // The shared instance defaults to JSON, which would make axios serialize
    // the FormData. In the browser axios drops this header again so the
    // boundary is set by the browser.
    headers: {
      'Content-Type': 'multipart/form-data',
      ...operatorRequestConfig(operator)?.headers,
    },
    onUploadProgress: (event) => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
    ...(signal ? { signal } : {}),
  });
  return data;
};

/**
 * Replaces one print file of a book ticket with an operator's PDF
 * (abo-print-files-upload). The file travels through DS as `multipart/form-data`
 * (field `file`); DS checks it, stores it next to the generated files and makes
 * it the file the print shop downloads. The generated file stays stored.
 *
 * Answers the ticket. A refusal is a 4xx with a `PRINT_FILE_*` code.
 *
 * Every print-file and ship-to change sends the signed-in operator's
 * FusionAuth id token in `x-afy-operator-token`: DS records the verified
 * operator as the uploader and refuses with `ABO_OPERATOR_UNVERIFIED` (401)
 * without it. No role is needed.
 */
export const uploadTicketFile = async (
  orderId: string,
  kind: TicketFileKind,
  file: File,
  operator: OperatorIdentity,
  onProgress?: (percent: number) => void,
): Promise<TicketDetail> =>
  postFile<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/files/${kind}`,
    file,
    operator,
    onProgress,
  );

/**
 * Replaces the member's flip book PDF with a whole-book PDF from a book ticket
 * (abo-print-files-flipbook-sync). DS finds the flip book itself and
 * afy-flipping reprocesses it in the background. Answers the ticket, or
 * `{orderId, replaced: true}` when the replace happened but DS could not read
 * the ticket back; the caller refetches the ticket either way.
 */
export const replaceTicketFlipBook = async (
  orderId: string,
  file: File,
  operator: OperatorIdentity,
  onProgress?: (percent: number) => void,
): Promise<TicketDetail | FlipBookReplaceAccepted> =>
  postFile<TicketDetail | FlipBookReplaceAccepted>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/flip-book`,
    file,
    operator,
    onProgress,
  );

/**
 * Makes the generated file current again for one print file of a book ticket.
 * The operator's upload stays stored. Answers the ticket.
 */
export const restoreTicketFile = async (
  orderId: string,
  kind: TicketFileKind,
  operator: OperatorIdentity,
): Promise<TicketDetail> => {
  const { data } = await instance.post<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/files/${kind}/restore`,
    undefined,
    operatorRequestConfig(operator),
  );
  return data;
};

/**
 * Corrects the ship-to of a book ticket and, with `regenerateInvoice`, asks DS
 * for a new invoice with that address in the same call
 * (abo-print-files-upload). An invoice failure still saves the address: the
 * result then says `invoiceRegenerated: false`. An invalid body is a 400
 * `SHIP_TO_INVALID`.
 */
export const updateTicketShipTo = async (
  orderId: string,
  body: UpdateShipToPayload,
  operator: OperatorIdentity,
): Promise<UpdateShipToResult> => {
  const { data } = await instance.patch<UpdateShipToResult>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/ship-to`,
    body,
    operatorRequestConfig(operator),
  );
  return data;
};

/**
 * Approves a book order on the member's behalf (book-order-approval F9), for
 * example after the member replied "I approve" to the e-mail. Allowed from
 * Awaiting approval and Changes requested; the order moves to Paid and the
 * printer is notified.
 *
 * Answers the ticket detail. A 409 means someone decided first (the member, or
 * another operator) and a 422 that the reason was rejected (3–500 characters).
 */
export const approveTicketOnMemberBehalf = async (
  orderId: string,
  payload: ApproveTicketPayload,
): Promise<TicketDetail> => {
  const { data } = await instance.post<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/approval/approve`,
    { reason: payload.reason.trim() },
  );
  return data;
};

/**
 * The cancel plan of a live book ticket, or the settle plan ("Return
 * credits") of an operator-cancelled one. Read-only and never cached by DS.
 *
 * Every cancel route needs the signed-in operator's FusionAuth id token in
 * `x-afy-operator-token`, and DS checks the `print_shop_cancel` role from it.
 * The headers go on THIS request only, never on the shared instance, because
 * DS also reads `x-afy-user-email` to resolve the member on some api-key
 * calls.
 */
export const getCancelPreview = async (
  orderId: string,
  operator: OperatorIdentity,
): Promise<BookCancelPreview> => {
  const { data } = await instance.get<BookCancelPreview>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/cancel/preview`,
    operatorRequestConfig(operator),
  );
  return data;
};

/**
 * Cancels a live book order from its current status (→ Cancelled; cannot be
 * undone here). A
 * `ledger` refund also needs the `credits_adjust` role. Answers the ticket
 * detail. Resending the same request after a failure completes it and never
 * returns the credits twice.
 */
export const cancelTicket = async (
  orderId: string,
  payload: CancelTicketPayload,
  operator: OperatorIdentity,
): Promise<TicketDetail> => {
  const body: CancelTicketPayload = {
    reason: payload.reason.trim(),
    refundMode: payload.refundMode,
  };
  if (payload.refundMode === 'ledger') {
    body.amountSource = payload.amountSource;
    body.amount = payload.amount;
  }
  if (payload.printerNoticeAcknowledged !== undefined) {
    body.printerNoticeAcknowledged = payload.printerNoticeAcknowledged;
  }
  const { data } = await instance.post<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/cancel`,
    body,
    operatorRequestConfig(operator),
  );
  return data;
};

/**
 * Returns the credits of an operator-cancelled book order after the credits
 * cutover ("Return credits"): the order's own ledger debit. Needs
 * `print_shop_cancel` and `credits_adjust`. Answers the ticket detail; a
 * second call on a settled ticket writes nothing.
 */
export const settleTicketRefund = async (
  orderId: string,
  payload: SettleTicketRefundPayload,
  operator: OperatorIdentity,
): Promise<TicketDetail> => {
  const body: SettleTicketRefundPayload = {
    amountSource: payload.amountSource,
    amount: payload.amount,
    noHubspotRestoreConfirmed: payload.noHubspotRestoreConfirmed,
  };
  const { data } = await instance.post<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/cancel/settle-refund`,
    body,
    operatorRequestConfig(operator),
  );
  return data;
};

/**
 * Re-sends the approval e-mail (book-order-approval F10): a new link valid for
 * 14 days, the old one stops working, and an order in Changes requested goes
 * back to Awaiting approval. Answers the ticket detail; 409 as above.
 */
export const resendApprovalEmail = async (
  orderId: string,
): Promise<TicketDetail> => {
  const { data } = await instance.post<TicketDetail>(
    `${TICKETS_PATH}/${encodeURIComponent(orderId)}/approval/resend`,
    {},
  );
  return data;
};

/** The Instant Trust Package routes of one book ticket (itp-mvp). */
const itpPath = (orderId: string): string =>
  `${TICKETS_PATH}/${encodeURIComponent(orderId)}/itp`;

/**
 * Adds one ITP PDF to a book ticket (itp-mvp): multipart field `file`, one
 * file per request, PDF only and up to 100 MB (checked in the browser first,
 * and again by DS). Allowed while the ITP is pending and the order waits
 * before Paid. Answers the ticket.
 *
 * Every ITP change sends the operator's id token in `x-afy-operator-token`,
 * like the print-file changes; DS records the verified operator. `signal`
 * aborts the upload (the card left the page).
 */
export const uploadItpFile = async (
  orderId: string,
  file: File,
  operator: OperatorIdentity,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<TicketDetail> =>
  postFile<TicketDetail>(
    `${itpPath(orderId)}/files`,
    file,
    operator,
    onProgress,
    signal,
  );

/** Removes one ITP file from the ticket (soft: DS keeps the object). Answers the ticket. */
export const removeItpFile = async (
  orderId: string,
  fileId: string,
  operator: OperatorIdentity,
): Promise<TicketDetail> => {
  const { data } = await instance.delete<TicketDetail>(
    `${itpPath(orderId)}/files/${encodeURIComponent(fileId)}`,
    operatorRequestConfig(operator),
  );
  return data;
};

/**
 * Marks the ITP ready (needs at least one file). The order moves to Paid and
 * the printer is released, unless the member's approval is still open. Safe
 * to repeat. Answers the ticket.
 */
export const markItpReady = async (
  orderId: string,
  operator: OperatorIdentity,
): Promise<TicketDetail> => {
  const { data } = await instance.post<TicketDetail>(
    `${itpPath(orderId)}/ready`,
    undefined,
    operatorRequestConfig(operator),
  );
  return data;
};

/**
 * A short-lived signed URL for one ITP file, minted PER CLICK and never
 * cached, like the print files. Needs the admin login only.
 */
export const fetchItpFileDownload = async (
  orderId: string,
  fileId: string,
): Promise<TicketFileDownload> => {
  const { data } = await instance.get<TicketFileDownload>(
    `${itpPath(orderId)}/files/${encodeURIComponent(fileId)}/download`,
  );
  return data;
};
